import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, skipToken } from '@apollo/client/react';
import { useSnackbar } from 'notistack';
import useAuth from './useAuth';
import {
    GET_IN_APP_NOTIFICATIONS,
    GET_UNSEEN_NOTIFICATION_COUNT,
} from '../graphql/resolvers/queries';
import { MARK_NOTIFICATIONS_SEEN, READ_NOTIFICATIONS } from '../graphql/resolvers/mutations';
import {
    InAppNotificationItem,
    InAppNotificationList,
    InAppNotificationPaginationCursor,
} from '../@types/notification.types';

const PAGE_SIZE = 20;

/**
 * A rejected session only surfaces as a GraphQL error, so it never reaches the 401 handling
 * in src/graphql/index.ts and the auth context keeps reporting a signed-in user. Left alone,
 * the bell would poll a dead session until the tab is closed.
 */
const isUnauthenticated = (error?: Error): boolean => Boolean(error?.message.includes('Unauthenticated'));

interface GetInAppNotificationsResponse {
    getInAppNotifsList: InAppNotificationList;
}

interface GetInAppNotificationsVariables {
    limit?: number;
    after?: InAppNotificationPaginationCursor | null;
}

interface GetUnseenNotificationCountResponse {
    getUnseenNotificationCount: number;
}

interface ReadNotifVariables {
    notificationIds: number[];
}

/**
 * Owns the in-app notification feed: cursor pagination, per-item read state
 * (tracked as local overrides, since `readNotif` returns only a boolean), and
 * the seen/unseen badge count used by the header bell.
 *
 * Uses `network-only`: `getInAppNotifsList` has a documented server-side write
 * side effect (read-on-view types are marked read as a consequence of being
 * returned), so a cache-served read would silently skip that marking.
 */
interface UseNotificationsOptions {
    /**
     * Enable polling on the base (first-page) query. Only safe for consumers
     * that never call `loadMore` — a poll tick refetches with the original
     * `{ limit }` variables, which would overwrite pages merged in by
     * `fetchMore`/`updateQuery`.
     */
    pollInterval?: number;
}

export default function useNotifications(
    pageSize: number = PAGE_SIZE,
    { pollInterval }: UseNotificationsOptions = {}
) {
    const { user } = useAuth();
    const { enqueueSnackbar } = useSnackbar();
    const [readOverrides, setReadOverrides] = useState<Set<number>>(new Set());
    const [sessionRejected, setSessionRejected] = useState(false);

    const activePollInterval = sessionRejected ? 0 : pollInterval;

    const { data, loading, error, refetch, fetchMore } = useQuery<
        GetInAppNotificationsResponse,
        GetInAppNotificationsVariables
    >(
        GET_IN_APP_NOTIFICATIONS,
        !user?.userId
            ? skipToken
            : {
                  variables: { limit: pageSize },
                  fetchPolicy: 'network-only',
                  notifyOnNetworkStatusChange: true,
                  pollInterval: activePollInterval,
              }
    );

    const {
        data: unseenData,
        error: unseenError,
        refetch: refetchUnseenCount,
    } = useQuery<GetUnseenNotificationCountResponse>(
        GET_UNSEEN_NOTIFICATION_COUNT,
        !user?.userId
            ? skipToken
            : { fetchPolicy: 'cache-and-network', pollInterval: activePollInterval }
    );

    const [markNotificationsSeen] = useMutation(MARK_NOTIFICATIONS_SEEN, {
        onError: (mutationError) => enqueueSnackbar(mutationError.message, { variant: 'error' }),
    });

    const [readNotif] = useMutation<{ readNotif: boolean }, ReadNotifVariables>(
        READ_NOTIFICATIONS,
        {
            onError: (mutationError) => enqueueSnackbar(mutationError.message, { variant: 'error' }),
        }
    );

    const rawItems = useMemo(() => data?.getInAppNotifsList?.items ?? [], [data]);
    const pageInfo = data?.getInAppNotifsList?.pageInfo;

    const items = useMemo<InAppNotificationItem[]>(
        () =>
            rawItems.map((item) =>
                !item.isRead && readOverrides.has(item.id) ? { ...item, isRead: true } : item
            ),
        [rawItems, readOverrides]
    );

    const unreadDelta = useMemo(
        () => rawItems.filter((item) => !item.isRead && readOverrides.has(item.id)).length,
        [rawItems, readOverrides]
    );

    const totalUnreadCount = Math.max(
        0,
        (data?.getInAppNotifsList?.totalUnreadCount ?? 0) - unreadDelta
    );
    const unseenCount = unseenData?.getUnseenNotificationCount ?? 0;

    const loadMore = useCallback(() => {
        if (!pageInfo?.hasNextPage || !pageInfo.endCursor) return;

        void fetchMore({
            variables: { limit: pageSize, after: pageInfo.endCursor },
            updateQuery: (previous, { fetchMoreResult }) => {
                if (!fetchMoreResult) return previous;

                const seen = new Set(previous?.getInAppNotifsList?.items.map((item) => item.id));
                const nextItems = fetchMoreResult.getInAppNotifsList.items.filter(
                    (item) => !seen.has(item.id)
                );

                return {
                    getInAppNotifsList: {
                        ...fetchMoreResult.getInAppNotifsList,
                        items: [...(previous?.getInAppNotifsList?.items ?? []), ...nextItems],
                    },
                };
            },
        });
    }, [fetchMore, pageInfo, pageSize]);

    const markRead = useCallback(
        (item: InAppNotificationItem) => {
            if (item.isRead || readOverrides.has(item.id)) return;

            setReadOverrides((prev) => new Set(prev).add(item.id));
            void readNotif({ variables: { notificationIds: [item.id] } });
        },
        [readNotif, readOverrides]
    );

    const markAllRead = useCallback(() => {
        const unreadIds = items.filter((item) => !item.isRead).map((item) => item.id);
        if (unreadIds.length === 0) return;

        setReadOverrides((prev) => {
            const next = new Set(prev);
            unreadIds.forEach((id) => next.add(id));
            return next;
        });
        void readNotif({ variables: { notificationIds: unreadIds } });
    }, [items, readNotif]);

    const markAllSeen = useCallback(() => {
        if (!user?.userId) return;
        void markNotificationsSeen().then(() => refetchUnseenCount());
    }, [markNotificationsSeen, refetchUnseenCount, user?.userId]);

    useEffect(() => {
        if (isUnauthenticated(error) || isUnauthenticated(unseenError)) setSessionRejected(true);
    }, [error, unseenError]);

    return {
        items,
        loading,
        error,
        hasNextPage: Boolean(pageInfo?.hasNextPage),
        totalUnreadCount,
        unseenCount,
        refetch,
        loadMore,
        markRead,
        markAllRead,
        markAllSeen,
    };
}
